import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Activity, AlertCircle, X, Trophy, Clock, CheckCircle2, XCircle, Mail } from 'lucide-react';
import { API } from '@/config';

const SubscribersActivity = ({ token, i18n }) => {
  const isAr = i18n?.language === 'ar';
  const t = (ar, sv, en) => (isAr ? ar : (i18n?.language === 'sv' ? sv : en));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState('all'); // all | active | dormant
  const [scope, setScope] = useState('subscribers');
  const [selectedUser, setSelectedUser] = useState(null);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/subscribers-activity?days=${days}&scope=${scope}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error(t('فشل تحميل البيانات', 'Kunde inte ladda data', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [days, scope]); // eslint-disable-line

  function openUser(user) {
    setSelectedUser(user);
    setHistory(null);
    setLoadingHistory(true);
    axios.get(`${API}/admin/users/${user.user_id}/test-history`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => setHistory(res.data))
      .catch(() => toast.error(t('فشل تحميل السجل', 'Kunde inte ladda historik', 'Failed to load history')))
      .finally(() => setLoadingHistory(false));
  }

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(isAr ? 'ar-EG' : 'sv-SE') + ' ' +
             d.toLocaleTimeString(isAr ? 'ar-EG' : 'sv-SE', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const relative = (iso) => {
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 5) return t('متصل الآن', 'Online nu', 'Online now');
    if (mins < 60) return t(`قبل ${mins} دقيقة`, `${mins} min sedan`, `${mins} min ago`);
    const h = Math.floor(mins / 60);
    if (h < 24) return t(`قبل ${h} ساعة`, `${h} tim sedan`, `${h}h ago`);
    const d = Math.floor(h / 24);
    return t(`قبل ${d} يوم`, `${d} dagar sedan`, `${d}d ago`);
  };

  const filtered = (data?.subscribers || []).filter(s => {
    if (filter === 'online') return s.is_online;
    if (filter === 'active') return s.is_active_recently;
    if (filter === 'dormant') return !s.is_active_recently;
    return true;
  });

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="subscribers-activity-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Activity className="w-6 h-6 text-emerald-600" />
            {t('نشاط المستخدمين', 'Användaraktivitet', 'User Activity')}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t('آخر ظهور لكل مستخدم مسجّل أو مشترك، ومن هو نشط الآن', 'Senast sedd för registrerade och prenumeranter', 'Last seen for registered users and subscribers')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600 dark:text-slate-300">
            {t('الفترة:', 'Period:', 'Period:')}
          </label>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
            data-testid="period-select"
          >
            <option value={7}>{t('آخر 7 أيام', 'Senaste 7 dagarna', 'Last 7 days')}</option>
            <option value={14}>{t('آخر 14 يوم', 'Senaste 14 dagarna', 'Last 14 days')}</option>
            <option value={30}>{t('آخر 30 يوم', 'Senaste 30 dagarna', 'Last 30 days')}</option>
            <option value={90}>{t('آخر 90 يوم', 'Senaste 90 dagarna', 'Last 90 days')}</option>
          </select>
        </div>
      </div>

      <div className="inline-flex rounded-xl bg-slate-100 dark:bg-slate-800 p-1" data-testid="scope-tabs">
        {[
          { id: 'subscribers', label: t('المشتركون', 'Prenumeranter', 'Subscribers') },
          { id: 'registered', label: t('المسجلون (بدون اشتراك)', 'Registrerade (utan prenumeration)', 'Registered (no plan)') },
          { id: 'all', label: t('الكل', 'Alla', 'All') },
        ].map(o => (
          <button key={o.id} onClick={() => setScope(o.id)} data-testid={`scope-${o.id}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${scope === o.id ? 'bg-white dark:bg-slate-700 text-emerald-700 dark:text-emerald-300 shadow-sm' : 'text-slate-500'}`}>
            {o.label}
          </button>
        ))}
      </div>

      {/* Stats cards */}
      {data?.stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatBox
            icon={Users}
            label={t('الإجمالي', 'Totalt', 'Total')}
            value={data.stats.total_subscribers}
            color="blue"
          />
          <StatBox
            icon={CheckCircle2}
            label={t(`نشطون (آخر ${days} يوم)`, `Aktiva (senaste ${days} dagarna)`, `Active (last ${days} days)`)}
            value={data.stats.active_recently}
            color="emerald"
          />
          <StatBox
            icon={Activity}
            label={t('متصلون الآن', 'Online nu', 'Online now')}
            value={data.stats.online_now || 0}
            color="blue"
          />
          <StatBox
            icon={AlertCircle}
            label={t('غير نشطين', 'Inaktiva', 'Dormant')}
            value={data.stats.dormant}
            color="amber"
          />
        </div>
      )}

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: t('الكل', 'Alla', 'All') },
          { id: 'online', label: t('متصلون الآن', 'Online nu', 'Online now') },
          { id: 'active', label: t('نشطون', 'Aktiva', 'Active') },
          { id: 'dormant', label: t('غير نشطين', 'Inaktiva', 'Dormant') },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
              filter === f.id
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200'
            }`}
            data-testid={`filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Subscribers table */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">{t('جارٍ التحميل...', 'Laddar...', 'Loading...')}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-slate-50 dark:bg-slate-900 rounded-xl">
          {t('لا يوجد مستخدمون مطابقون', 'Inga matchande användare', 'No matching users')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
              <tr>
                <th className="text-start px-4 py-3 font-semibold">{t('المستخدم', 'Användare', 'User')}</th>
                <th className="text-start px-4 py-3 font-semibold">{t('الباقة', 'Plan', 'Plan')}</th>
                <th className="text-center px-4 py-3 font-semibold">{t('اختبارات', 'Test', 'Tests')}</th>
                <th className="text-center px-4 py-3 font-semibold">{t(`آخر ${days} يوم`, `Senaste ${days}d`, `Last ${days}d`)}</th>
                <th className="text-center px-4 py-3 font-semibold">{t('متوسط', 'Snitt', 'Avg')}</th>
                <th className="text-start px-4 py-3 font-semibold">{t('آخر ظهور', 'Senast sedd', 'Last seen')}</th>
                <th className="text-start px-4 py-3 font-semibold">{t('آخر تسجيل دخول', 'Senaste inloggning', 'Last login')}</th>
                <th className="text-center px-4 py-3 font-semibold">{t('الحالة', 'Status', 'Status')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr
                  key={s.user_id}
                  onClick={() => openUser(s)}
                  className="border-t border-slate-100 dark:border-slate-700 hover:bg-emerald-50 dark:hover:bg-slate-800 cursor-pointer transition"
                  data-testid={`subscriber-row-${s.user_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800 dark:text-slate-100">{s.name || '—'}</div>
                    <div className="text-xs text-slate-500">{s.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                      {s.is_subscriber ? (s.subscription_plan || 'taxi') : t('مسجّل', 'Registrerad', 'Registered')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold">{s.total_tests}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-bold ${s.tests_last_period > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {s.tests_last_period}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {s.avg_score != null ? (
                      <span className={`font-semibold ${s.avg_score >= 70 ? 'text-emerald-600' : s.avg_score >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                        {s.avg_score}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {s.last_activity ? (
                      <>
                        <div>{formatDate(s.last_activity)}</div>
                        <div className={s.is_online ? 'text-emerald-600 font-semibold' : 'text-slate-400'} data-testid={`last-seen-rel-${s.user_id}`}>{relative(s.last_activity)}</div>
                      </>
                    ) : <span className="text-slate-400">{t('لم يدخل أبداً', 'Aldrig', 'Never')}</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {s.last_login ? formatDate(s.last_login) : '—'}
                    {s.login_count > 0 && <div className="text-slate-400">{s.login_count}×</div>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {s.is_online ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500 text-white">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                        {t('متصل', 'Online', 'Online')}
                      </span>
                    ) : s.is_active_recently ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                        {t('نشط', 'Aktiv', 'Active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        {t('نائم', 'Vilande', 'Dormant')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User detail modal */}
      {selectedUser && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedUser(null)}
          data-testid="user-history-modal"
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  {selectedUser.name || selectedUser.email}
                </h3>
                <p className="text-xs opacity-90">{selectedUser.email}</p>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="p-2 rounded-lg hover:bg-white/10 transition"
                data-testid="close-history"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {loadingHistory ? (
                <div className="text-center py-12 text-slate-500">{t('جارٍ التحميل...', 'Laddar...', 'Loading...')}</div>
              ) : history ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <MiniStat label={t('الإجمالي', 'Totalt', 'Total')} value={history.stats.total} icon={Activity} color="blue" />
                    <MiniStat label={t('مكتملة', 'Klart', 'Completed')} value={history.stats.completed} icon={CheckCircle2} color="emerald" />
                    <MiniStat label={t('غادر', 'Avbrutna', 'Abandoned')} value={history.stats.abandoned} icon={XCircle} color="amber" />
                    <MiniStat label={t('أفضل', 'Bästa', 'Best')} value={`${history.stats.best_score}%`} icon={Trophy} color="purple" />
                  </div>
                  {history.sessions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <Clock className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      {t('هذا المشترك لم يبدأ أي اختبار بعد', 'Denna prenumerant har inte påbörjat något test', 'This subscriber has not started any test yet')}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {history.sessions.map((s) => (
                        <div
                          key={s.id}
                          className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                          data-testid="history-session"
                        >
                          <div>
                            <div className="font-semibold text-sm text-slate-800 dark:text-slate-100">
                              {s.test_name || s.test_id}
                            </div>
                            <div className="text-xs text-slate-500">
                              {formatDate(s.started_at)} · {s.language?.toUpperCase()}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {s.score_percentage != null && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                s.score_percentage >= 70 ? 'bg-emerald-100 text-emerald-700' :
                                s.score_percentage >= 50 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {s.score_percentage}%
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              s.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              s.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {s.status === 'completed' ? t('مكتمل', 'Klart', 'Done')
                               : s.status === 'in_progress' ? t('جاري', 'Pågår', 'In progress')
                               : t('غادر', 'Avbrutet', 'Abandoned')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatBox = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-orange-600',
  };
  return (
    <div className="p-4 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-gradient-to-br ${colors[color]} text-white`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{value}</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, icon: Icon, color }) => {
  const c = { blue: 'text-blue-600', emerald: 'text-emerald-600', amber: 'text-amber-600', purple: 'text-purple-600' }[color];
  return (
    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-center">
      <Icon className={`w-4 h-4 mx-auto mb-1 ${c}`} />
      <div className="text-lg font-bold text-slate-800 dark:text-slate-100">{value}</div>
      <div className="text-[10px] text-slate-500">{label}</div>
    </div>
  );
};

export default SubscribersActivity;
